import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  getChatToken,
  getMembersChatToken,
  getMyChatChannels,
  getChannelMessages,
  type MemberInfo,
  getChatLiability,
} from "@/services/csr-chat/csr-chat-service";
import {
  CSR_CHAT_QUERY_KEYS,
  CSR_CHAT_QUERY_RETRY,
  CSR_SOCKET_APP_ID,
  CSR_SOCKET_USER_ROLE,
  CSR_SOCKET_USER_TYPE,
  CSR_TYPING_DEBOUNCE_MS,
  CSR_TYPING_IDLE_MS,
} from "@/services/csr-chat/csr-chat-constants";
import {
  connectSocket,
  emitUserTypingStatus,
} from "@/services/csr-chat/socket-service";
import { initChatSocketBridge } from "@/services/csr-chat/chat-socket-bridge";
import useUserInfo from "@/hooks/use-user-info";
import { store, type RootState } from "@/store/store";
import {
  mergeMessages,
  setChannelOldCount,
  setChannelMessageRef2,
} from "@/store/csr-chat/csr-chat-slice";

export * from "@/model/csr-chat";
export { getChatToken } from "@/services/csr-chat/csr-chat-service";

const CSR_CHAT_API = process.env.CSR_CHAT ?? "";

const isInvalidAccess = async (err: unknown): Promise<boolean> => {
  if (!axios.isAxiosError(err)) return false;
  if (err.response?.status === 403) return true;
  const data = err.response?.data;
  try {
    const text =
      data instanceof Blob ? await data.text() : JSON.stringify(data);
    return JSON.parse(text)?.message === "Invalid access token";
  } catch {
    return false;
  }
};

type UploadResponseData = {
  key: string;
  originalName: string;
  fileType: string;
  size: number;
};

const useMemberInfo = (): MemberInfo => {
  const userInfo = useUserInfo();
  return {
    username: userInfo.username,
    groupId: userInfo.groupId,
    divisionId: userInfo.divisionId,
    memberId: userInfo.memberId,
  };
};

export const useChatInfo = (enabled: boolean = true) => {
  const memberInfo = useMemberInfo();
  const { data: tokenData } = useMembersChatToken(enabled);

  return useQuery({
    queryKey: [CSR_CHAT_QUERY_KEYS.LIABILITY],
    queryFn: () => getChatLiability(memberInfo),
    enabled: enabled && Boolean(tokenData?.accessCode),
    retry: CSR_CHAT_QUERY_RETRY,
  });
};

export const useMembersChatToken = (enabled = true) => {
  const memberInfo = useMemberInfo();
  return useQuery({
    queryKey: [CSR_CHAT_QUERY_KEYS.TOKEN],
    queryFn: () => getMembersChatToken(memberInfo),
    enabled:
      enabled && Boolean(memberInfo.username) && Boolean(memberInfo.memberId),
    retry: CSR_CHAT_QUERY_RETRY,
    refetchOnMount: "always",
  });
};

export const useChatChannels = (enabled = true) => {
  const memberInfo = useMemberInfo();
  const { data: tokenData } = useMembersChatToken(enabled);

  return useQuery({
    queryKey: [CSR_CHAT_QUERY_KEYS.CHANNELS],
    queryFn: () => getMyChatChannels(memberInfo),
    enabled: enabled && Boolean(tokenData?.accessCode),
    retry: CSR_CHAT_QUERY_RETRY,
  });
};

async function withTokenRetry<T>(
  request: () => Promise<T>,
  info: MemberInfo,
): Promise<T> {
  try {
    return await request();
  } catch (err) {
    if (!(await isInvalidAccess(err))) throw err;
    await getMembersChatToken(info);
    return await request();
  }
}

export const useChannelMessages = (chatChannel: string) => {
  const memberInfo = useMemberInfo();
  const memberInfoRef = useRef(memberInfo);
  useEffect(() => {
    memberInfoRef.current = memberInfo;
  });

  const { data: tokenData } = useMembersChatToken();
  const dispatch = useDispatch();

  const oldCount = useSelector(
    (state: RootState) => state?.csrChat?.channelOldCounts[chatChannel] ?? 0,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const query = useQuery({
    queryKey: [CSR_CHAT_QUERY_KEYS.MESSAGES, chatChannel],
    queryFn: () =>
      withTokenRetry(
        () => getChannelMessages({ chatChannel }, memberInfoRef.current),
        memberInfoRef.current,
      ),
    enabled: Boolean(tokenData?.accessCode) && Boolean(chatChannel),
    retry: CSR_CHAT_QUERY_RETRY,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!query.data || !chatChannel) return;
    dispatch(mergeMessages({ channelId: chatChannel, msgs: query.data.data }));
    const alreadyInitialised = Boolean(
      store.getState()?.csrChat?.channelMessageRef2?.[chatChannel],
    );
    if (!alreadyInitialised) {
      dispatch(
        setChannelOldCount({
          channelId: chatChannel,
          count: query.data.oldCount,
        }),
      );
      if (query.data.data.length > 0) {
        dispatch(
          setChannelMessageRef2({
            channelId: chatChannel,
            ref: query.data.data[query.data.data.length - 1]._id,
          }),
        );
      }
    }
  }, [query.data, chatChannel, dispatch]);

  const loadMore = useCallback(async () => {
    const state = store.getState()?.csrChat;
    if (!state) return;
    const messageRef2 = state.channelMessageRef2?.[chatChannel];
    const currentOldCount = state.channelOldCounts?.[chatChannel] ?? 0;
    if (!messageRef2 || currentOldCount === 0 || isLoadingMore || !chatChannel)
      return;

    const storeMessages = state.messages?.[chatChannel] ?? [];
    if (storeMessages.length === 0) return;

    setIsLoadingMore(true);
    try {
      const result = await withTokenRetry(
        () =>
          getChannelMessages(
            {
              chatChannel,
              messageRef1: storeMessages[0]._id,
              messageRef2,
              order: -1,
            },
            memberInfoRef.current,
          ),
        memberInfoRef.current,
      );
      dispatch(mergeMessages({ channelId: chatChannel, msgs: result.data }));
      dispatch(
        setChannelOldCount({ channelId: chatChannel, count: result.oldCount }),
      );
    } catch {
      // leave existing messages intact on failure
    } finally {
      setIsLoadingMore(false);
    }
  }, [chatChannel, isLoadingMore, dispatch]);

  return { ...query, oldCount, isLoadingMore, loadMore };
};

export const useUploadAttachment = (
  chatChannel: string,
  tempMessageId?: string,
) => {
  const memberInfo = useMemberInfo();
  const [uploadProgress, setUploadProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      abortRef.current = new AbortController();
      setUploadProgress(0);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("chatChannel", chatChannel);
      if (tempMessageId) formData.append("tempMessageId", tempMessageId);

      const doRequest = (token: string) =>
        axios<{ statusCode: number; data: UploadResponseData }>({
          method: "post",
          headers: {
            group_id: memberInfo.groupId,
            Authorization: `Bearer ${token}`,
          },
          url: `${CSR_CHAT_API}/attachment/uploadFile`,
          signal: abortRef.current!.signal,
          data: formData,
          onUploadProgress: (e) =>
            setUploadProgress(Math.round((e.loaded / (e.total ?? 1)) * 100)),
        });

      let res: Awaited<ReturnType<typeof doRequest>>;
      try {
        res = await doRequest(getChatToken());
      } catch (err) {
        if (!(await isInvalidAccess(err))) throw err;
        const { accessCode } = await getMembersChatToken(memberInfo);
        setUploadProgress(0);
        abortRef.current = new AbortController();
        res = await doRequest(accessCode);
      }

      const { statusCode, data } = res.data;
      if (
        statusCode !== 200 ||
        !data?.key ||
        !data?.originalName ||
        !data?.fileType ||
        !data?.size
      ) {
        throw new Error("Upload failed");
      }
      setUploadProgress(100);
      return data;
    },
  });

  return { ...mutation, uploadProgress };
};

export const useDownloadAttachment = () => {
  const memberInfo = useMemberInfo();
  const [downloadProgress, setDownloadProgress] = useState(0);

  const mutation = useMutation({
    mutationFn: async (s3Key: string) => {
      setDownloadProgress(0);

      const doRequest = (token: string) =>
        axios({
          method: "post",
          headers: {
            "Content-Type": "application/json",
            group_id: memberInfo.groupId,
            Authorization: `Bearer ${token}`,
            username: memberInfo.username,
          },
          url: `${CSR_CHAT_API}/attachment/getFile`,
          data: { key: s3Key },
          responseType: "blob" as const,
          onDownloadProgress: (e) =>
            setDownloadProgress(Math.round((e.loaded / (e.total ?? 1)) * 100)),
        });

      try {
        return (await doRequest(getChatToken())).data as Blob;
      } catch (err) {
        if (!(await isInvalidAccess(err))) throw err;
        const { accessCode } = await getMembersChatToken(memberInfo);
        return (await doRequest(accessCode)).data as Blob;
      }
    },
  });

  return { ...mutation, downloadProgress };
};

export const useCsrTyping = (channelId: string) => {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const channelIdRef = useRef(channelId);

  useEffect(() => {
    channelIdRef.current = channelId;
  }, [channelId]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      if (channelIdRef.current) {
        emitUserTypingStatus({
          chatChannel: channelIdRef.current,
          isTyping: false,
        });
      }
      isTypingRef.current = false;
    }, CSR_TYPING_IDLE_MS);
  }, []);

  const stopTyping = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    debounceTimerRef.current = null;
    idleTimerRef.current = null;
    if (isTypingRef.current && channelIdRef.current) {
      emitUserTypingStatus({
        chatChannel: channelIdRef.current,
        isTyping: false,
      });
    }
    isTypingRef.current = false;
  }, []);

  const onTypingInput = useCallback(
    (value: string) => {
      if (!channelIdRef.current) return;
      if (!value) {
        stopTyping();
        return;
      }
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        emitUserTypingStatus({
          chatChannel: channelIdRef.current,
          isTyping: true,
        });
        resetIdleTimer();
      } else {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        resetIdleTimer();
        debounceTimerRef.current = setTimeout(() => {
          emitUserTypingStatus({
            chatChannel: channelIdRef.current,
            isTyping: true,
          });
        }, CSR_TYPING_DEBOUNCE_MS);
      }
    },
    [stopTyping, resetIdleTimer],
  );

  return { onTypingInput, stopTyping };
};

export const useChatSocketInit = (enabled = true): void => {
  const userInfo = useUserInfo();
  const { data: tokenData } = useMembersChatToken(enabled);

  useEffect(() => {
    const token = tokenData?.accessCode;
    if (!enabled || !token || !userInfo.username || !userInfo.memberId) return;

    connectSocket(token, {
      app_id: CSR_SOCKET_APP_ID,
      username: userInfo.username,
      group_id: userInfo.groupId,
      division_id: userInfo.divisionId,
      first_name: userInfo.firstname,
      last_name: userInfo.lastname,
      phone_no: userInfo.phone,
      member_id: userInfo.memberId,
      user_role: CSR_SOCKET_USER_ROLE,
      user_type: CSR_SOCKET_USER_TYPE,
      device_token: "",
      os: "",
      model: "",
      brand: "",
      app_version: "",
      user_agent: navigator.userAgent,
      system_version: "",
      build_number: "",
      group_name: userInfo.groupName,
      email: userInfo.email,
      icon: "",
      mbr_id: userInfo.mbrId,
    });

    initChatSocketBridge(userInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenData?.accessCode, userInfo.memberId, enabled]);
};
